import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { useSettings } from '../../context/SettingsContext';
import { useUI } from '../../context/UIContext';
import { IconMail, IconArrowRight, IconFacebook, IconInstagram, IconTiktok, IconYoutube, IconTwitter, IconPinterest, IconLinkedin } from '../common/Icons';
import { NavAnchor } from './Header';

const SOCIAL_ICONS = { facebook: IconFacebook, instagram: IconInstagram, tiktok: IconTiktok, youtube: IconYoutube, twitter: IconTwitter, pinterest: IconPinterest, linkedin: IconLinkedin };

export function SocialLinks({ className = '' }) {
  const { settings } = useSettings();
  const social = settings.social || {};
  const entries = Object.entries(social).filter(([k, v]) => v && SOCIAL_ICONS[k]);
  if (!entries.length) return null;
  return (
    <div className={`social-media-links flex items-center px-2 ${className}`}>
      {entries.map(([k, v]) => {
        const Icon = SOCIAL_ICONS[k];
        return (
          <a key={k} href={v} target="_blank" rel="noreferrer" className="px-2 flex items-center" aria-label={k}>
            <Icon width={16} height={16} />
          </a>
        );
      })}
    </div>
  );
}

export function NewsletterForm({ source = 'footer', className = '' }) {
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(true);
  const [busy, setBusy] = useState(false);
  const { toast } = useUI();
  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast('Please agree to the terms', 'error');
    setBusy(true);
    try {
      const r = await api.post('/contact/subscribe', { email, source });
      toast(r.message);
      setEmail('');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setBusy(false);
    }
    return null;
  };
  return (
    <form className={`sf__footer-form-newsletter form__style-bordered ${className}`} onSubmit={submit}>
      <div className="relative">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="form-control form__control-icon py-1.5 px-12 sf__newletter__form-input" placeholder="Enter your email" aria-label="Email" />
        <span className="sf__footer-form-newsletter-icon absolute"><IconMail width={18} height={18} /></span>
        <button type="submit" className="sf__form-submit absolute" disabled={busy} aria-label="Subscribe"><IconArrowRight width={18} height={18} /></button>
      </div>
      <div className="agree-terms mt-2">
        <label className="flex items-center text-sm cursor-pointer">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mr-2" />
          <span className="flex">
            <p className="mr-1">I agree with the</p>
            <Link to="/pages/terms-of-service" className="underline">Terms & conditions</Link>
          </span>
        </label>
      </div>
    </form>
  );
}

function ColumnBody({ col }) {
  const { settings } = useSettings();
  const g = settings.general || {};
  if (col.type === 'contact') {
    return (
      <div className="sf__accordion-content rte">
        <div className="pb-5 md:pb-0">
          {col.text && <p className="mt-0 md:mt-4 block-text" dangerouslySetInnerHTML={{ __html: col.text }} />}
          {g.address && <p className="mt-2">{g.address}</p>}
          <div className="block-text mt-4">
            {g.phone && <p><a className="hover:text-black" href={`tel:${g.phone.replace(/[^\d+]/g, '')}`}>{g.phone}</a></p>}
            {g.email && <p className="mt-1"><a className="hover:text-black" href={`mailto:${g.email}`}>{g.email}</a></p>}
            {g.openingHours && <p className="mt-1">{g.openingHours}</p>}
          </div>
        </div>
      </div>
    );
  }
  if (col.type === 'newsletter') {
    return (
      <div className="sf__accordion-content rte">
        <div className="pb-5 md:pb-0">
          <p className="py-2 block-text sf__newsletter-block-desc">{col.text || settings.footer?.newsletterText}</p>
          <NewsletterForm />
          {settings.footer?.showSocial !== false && (
            <div className="block -mx-4 mt-4"><SocialLinks /></div>
          )}
        </div>
      </div>
    );
  }
  if (col.type === 'text') {
    return (
      <div className="sf__accordion-content rte">
        <div className="pb-5 md:pb-0" dangerouslySetInnerHTML={{ __html: col.text }} />
      </div>
    );
  }
  return (
    <ul className="sf__accordion-content">
      <div className="pb-5 md:pb-0">
        {(col.links || []).map((l, i) => (
          <li key={i} className="link-lv2 leading-8 hover-underline"><NavAnchor to={l.url}>{l.label}</NavAnchor></li>
        ))}
      </div>
    </ul>
  );
}

const PAYMENT_LABELS = { visa: 'VISA', mastercard: 'MasterCard', amex: 'AMEX', paypal: 'PayPal', discover: 'Discover', applepay: 'Apple Pay', googlepay: 'G Pay', upi: 'UPI', cod: 'COD' };

export default function Footer() {
  const { settings } = useSettings();
  const f = settings.footer || {};
  const cols = f.columns || [];
  const [open, setOpen] = useState(() => cols.findIndex((c) => c.type === 'newsletter'));
  const widthClass = cols.length >= 5 ? 'lg:w-1/5' : cols.length === 4 ? 'lg:w-1/4' : cols.length === 3 ? 'lg:w-1/3' : 'lg:w-1/2';

  return (
    <footer className="sf-footer" style={{ '--color-footer-background': f.background || '#2d2d2d', '--color-footer-bottom-background': f.bottomBackground || '#363636' }}>
      <div className="sf__footer-middle">
        <div className="container-fluid">
          {/* Desktop columns */}
          <div className="hidden md:flex md:flex-wrap lg:flex-nowrap -mx-5">
            {cols.map((col, i) => (
              <div key={i} className={`sf-footer__block sf__footer-block-${col.type} ${widthClass} md:w-1/2`}>
                <div className="sf-footer__block-inner px-5">
                  <h3 className="font-medium mb-4 text-base sf__footer-block-title">{col.title}</h3>
                  <div className="eg-footer-open"><ColumnBody col={col} /></div>
                </div>
              </div>
            ))}
          </div>
          {/* Mobile accordion */}
          <div className="sf-footer__accordion px-4 pt-8 md:pt-10 flex md:hidden flex-col">
            {cols.map((col, i) => (
              <div key={i} className={`sf__accordion-item mb-2 overflow-hidden ${open === i ? 'open' : ''} ${col.type === 'newsletter' ? 'order-first' : ''}`}>
                <h3 className="sf__accordion-button font-medium md:mb-4 text-base sf__footer-block-title" onClick={() => setOpen(open === i ? -1 : i)} role="button">{col.title}</h3>
                <div className="eg-footer-acc" style={{ maxHeight: open === i ? 900 : 0, opacity: open === i ? 1 : 0, transition: 'all .3s' }}>
                  <ColumnBody col={col} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="footer-layout sf__footer-bottom mt-5 md:mt-0">
        <div className="container-fluid">
          <div className="sf__footer-bottom-inner lg:pb-8 pt-0 lg:py-5 flex flex-col lg:flex-row justify-between items-center py-5">
            <div className="sf-footer__left flex-1 text-center w-full md:text-left mb-6 lg:mb-0 lg:w-auto lg:order-none">
              <span className="text-sm text-left"><span style={{ fontSize: '1rem' }}>{f.bottomText || settings.general?.copyright || `© ${settings.general?.siteName} ${new Date().getFullYear()}`}</span></span>
            </div>
            <div className="sf-footer__right w-full flex flex-wrap justify-center md:justify-start lg:justify-center items-center lg:w-auto gap-2">
              {(f.paymentIcons || []).map((p) => (
                <span key={p} className="eg-payment-icon" title={PAYMENT_LABELS[p] || p}>{PAYMENT_LABELS[p] || p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
